import type { ReactNode } from 'react'
import { Check, CircleDashed } from 'lucide-react'
import {
  completeBadge,
  description,
  item,
  marker,
  tag,
  text,
  title,
  trail,
  trailVertical,
} from './StepTrail.css'

export type StepTrailItem = {
  id: string
  title: string
  description?: string
  icon?: ReactNode
  status?: 'complete' | 'current' | 'pending' | 'incomplete'
  tagLabel?: string
  visible?: boolean
  disabled?: boolean
}

export type StepTrailProps = {
  steps: StepTrailItem[]
  currentStep?: number
  activeId?: string
  orientation?: 'horizontal' | 'vertical'
  onStepClick?: (id: string) => void
}

function getTagTone(step: StepTrailItem, state: StepTrailItem['status'] | 'hidden') {
  if (step.tagLabel === 'Configurado') return 'success' as const
  if (step.tagLabel === 'Incompleto') return 'awaiting' as const
  if (state === 'complete') return 'success' as const
  if (state === 'incomplete') return 'awaiting' as const
  return 'neutral' as const
}

export function StepTrail({
  steps,
  currentStep,
  activeId,
  orientation = 'horizontal',
  onStepClick,
}: StepTrailProps) {
  const layout = orientation === 'vertical' ? 'compact' : 'default'

  return (
    <nav className={orientation === 'vertical' ? trailVertical : trail} aria-label="Etapas">
      {steps.map((step, index) => {
        const stepNumber = steps.slice(0, index + 1).filter((item) => item.visible !== false).length
        const state =
          step.visible === false
            ? 'hidden'
            : (step.status ??
              (activeId
                ? step.id === activeId
                  ? 'current'
                  : 'pending'
                : currentStep !== undefined && stepNumber < currentStep
                  ? 'complete'
                  : currentStep !== undefined && stepNumber === currentStep
                    ? 'current'
                    : 'pending'))
        const interactive = Boolean(onStepClick) && !step.disabled && state !== 'hidden'
        const markerContent = (
          <>
            {step.icon ??
              (state === 'incomplete' ? <CircleDashed size={14} strokeWidth={2.4} /> : stepNumber)}
            {state === 'complete' ? (
              <span className={completeBadge} aria-hidden="true">
                <Check size={8} strokeWidth={3.2} />
              </span>
            ) : null}
          </>
        )
        const content = (
          <>
            <span className={marker({ state, layout })}>{markerContent}</span>
            <span className={text}>
              <span className={title({ state })}>{step.title}</span>
              {step.description ? (
                <span className={description({ layout })}>{step.description}</span>
              ) : null}
              {step.tagLabel ? (
                <span
                  className={tag({
                    layout,
                    tone: getTagTone(step, state),
                  })}
                >
                  {step.tagLabel}
                </span>
              ) : null}
            </span>
          </>
        )

        return interactive ? (
          <button
            key={step.id}
            type="button"
            className={item({ state, interactive, layout })}
            aria-current={state === 'current' ? 'step' : undefined}
            onClick={() => onStepClick?.(step.id)}
          >
            {content}
          </button>
        ) : (
          <div
            key={step.id}
            className={item({ state, interactive, layout })}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            {content}
          </div>
        )
      })}
    </nav>
  )
}
